import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ConfirmModal = () => {
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleOpen = (e) => {
      setModal(e.detail);
      setLoading(false);
    };

    const handleClose = () => {
      setModal(null);
      setLoading(false);
    };

    window.addEventListener('openConfirmModal', handleOpen);
    window.addEventListener('closeConfirmModal', handleClose);
    window.closeConfirmModal = handleClose;

    return () => {
      window.removeEventListener('openConfirmModal', handleOpen);
      window.removeEventListener('closeConfirmModal', handleClose);
    };
  }, []);

  if (!modal) return null;

  const {
    icon,
    iconBg = '#fef7f7',
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmColor = '#ea4335',
    confirmHoverColor = '#d33426',
    onConfirm,
    onCancel
  } = modal;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (onConfirm) await onConfirm();
    } catch (e) {
      console.error('Confirm action failed:', e);
    }
    setLoading(false);
    setModal(null);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    setModal(null);
  };

  return createPortal(
    <div
      onClick={handleCancel}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20000,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        {/* Icon */}
        <div style={{
          width: '56px',
          height: '56px',
          background: iconBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          fontSize: '28px'
        }}>
          {icon}
        </div>

        {/* Title */}
        <h3 style={{
          textAlign: 'center',
          margin: '16px 0 8px',
          fontSize: '18px',
          fontWeight: 600,
          color: '#202124'
        }}>
          {title}
        </h3>

        {/* Message */}
        <p style={{
          textAlign: 'center',
          margin: '0 0 24px',
          fontSize: '14px',
          color: '#5f6368',
          lineHeight: 1.5
        }}>
          {message}
        </p>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={handleCancel}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              background: 'white',
              fontSize: '14px',
              fontWeight: 600,
              color: '#5f6368',
              cursor: 'pointer'
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            onMouseOver={(e) => { if (!loading) e.target.style.background = confirmHoverColor; }}
            onMouseOut={(e) => { if (!loading) e.target.style.background = confirmColor; }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background: loading ? '#ccc' : confirmColor,
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Please wait...' : confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ConfirmModal;